import { isPercentageCartItem, isFixedCartItem, type AllowedCartItem, type DefaultCartMeta } from '../types/cart';

/**
 * A composable that provides the base functionality for a shopping cart.
 * It manages the cart's state (items) and exposes actions to manipulate the cart.
 * It also consumes other composables to provide derived data like total and display items.
 *
 * @param initialItems - An optional array of initial items in the cart.
 * @returns An object with the cart's state and actions.
 */
export function useCart<TExtra extends DefaultCartMeta = DefaultCartMeta>(initialItems: Array<AllowedCartItem<TExtra>> = []) {
  const items = shallowRef<Array<AllowedCartItem<TExtra>>>(initialItems);

  const itemsCount = computed(() => items.value.length);
  const hasItems = computed(() => itemsCount.value > 0);

  function itemExists(item: Pick<AllowedCartItem, 'id'>): boolean {
    return items.value.some((i) => i.id === item.id);
  }

  function addItem(item: AllowedCartItem<TExtra>): boolean {
    if (!itemExists(item)) {
      items.value = [...items.value, item];
      return true;
    }
    return false;
  }

  function setItems(newItems: Array<AllowedCartItem<TExtra>>): boolean {
    items.value = newItems;
    return true;
  }

  function removeItem(cartItemId: string): boolean {
    const newItems = items.value.filter((i) => i.id !== cartItemId);
    if (newItems.length < items.value.length) {
      items.value = newItems;
      return true;
    }
    return false;
  }

  function updateItem(cartItemId: string, updates: Partial<AllowedCartItem<TExtra>>): boolean {
    const index = items.value.findIndex((i) => i.id === cartItemId);

    if (index > -1) {
      const updatedItem = { ...items.value[index], ...updates } as AllowedCartItem<TExtra>;

      if (isFixedCartItem(updatedItem) || isPercentageCartItem(updatedItem)) {
        const newItems = [...items.value];
        newItems[index] = updatedItem;
        items.value = newItems;
        return true;
      }
    }
    return false;
  }

  /**
   * Updates the quantity of a static cart item.
   * Only static items can have their quantity changed.
   *
   * @param cartItemId - The ID of the cart item to update.
   * @param quantity - The new quantity. Must be a positive number or null.
   * @returns true if the quantity was updated successfully, false otherwise.
   */
  function updateItemQuantity(cartItemId: string, quantity: number | null): boolean {
    const item = getItemById(cartItemId);
    const isItemInvalid = !item || !isFixedCartItem(item);
    const isQuantityInvalid = quantity !== null && (quantity <= 0 || !Number.isInteger(quantity));

    if (isItemInvalid || isQuantityInvalid) {
      return false;
    }

    return updateItem(cartItemId, { quantity } as Partial<AllowedCartItem<TExtra>>);
  }

  function clearItems(): void {
    setItems([]);
  }

  function getItemById(cartItemId: string): AllowedCartItem<TExtra> | undefined {
    return items.value.find((i) => i.id === cartItemId);
  }

  function removeItemsByGroup(groupId: string): boolean {
    const newItems = items.value.filter((i) => i.group !== groupId);
    if (newItems.length < items.value.length) {
      items.value = newItems;
      return true;
    }
    return false;
  }

  return {
    items,
    itemsCount,
    hasItems,
    addItem,
    setItems,
    removeItem,
    updateItem,
    updateItemQuantity,
    clearItems,
    itemExists,
    getItemById,
    removeItemsByGroup,
  };
}